<template>
  <TopBannerV2 @ppp-available="onPppAvailable" @discount-available="onDiscountAvailable" />

  <div class="w-full flex flex-col bg-white">
    <!-- only here for the anchor -->
    <div id="home"></div>

    <div class="bg-black pt-40 px-2 pb-8 text-white">
      <RestrictedWidth>
        <HeroSection />
      </RestrictedWidth>
    </div>

    <RestrictedWidth class="my-8">
      <section class="mt-8 mb-16 rounded-3xl bg-slate-900 px-8 py-8 text-white ring-1 ring-slate-800">
        <p class="text-sm font-bold uppercase tracking-wide text-yellow-300">
          Why this still matters in the AI era
        </p>

        <h2 class="mt-3 text-2xl sm:text-4xl font-black tracking-tight">
          AI can help you write serverless code faster.
          <span class="text-yellow-300">It can also help you ship production mistakes faster.</span>
        </h2>

        <div class="mt-2 text-lg sm:text-xl leading-8 text-slate-300 flex flex-col gap-4">
          <p>
            AI can generate Lambda handlers, CDK snippets, test examples, and IAM policies.
            But it will not reliably choose the right architecture boundaries, failure handling strategy,
            event design, testing approach, deployment model, or operational safeguards for your team.
          </p>

          <p>
            This bootcamp teaches you how to judge those decisions, so AI becomes an accelerator,
            not a source of hidden problems in production.
          </p>
        </div>
      </section>

      <TestimonialSlim image="julian-bishop.jpg" platform="linkedin" url="https://www.linkedin.com/in/julian-bishop-91812a9" class="m5-10">
        <template v-slot:quote>
          <p class="pl-3 pt-2">"Before taking the class, I tried using ChatGPT to create a syllabus for me - Yan's class is 1000% better. <span class='underline underline-offset-2 text-red-500'>I can't recommend this course highly enough</span>."</p>
        </template>
        <template v-slot:author>
          <p class="pl-3 flex items-center">- Julian Bishop, Technology Director at <img src="https://res.cloudinary.com/prsls/image/upload/f_auto,c_scale,h_64/website/logo-wide-dish.png" class="h-10 inline-block" /></p>
        </template>
      </TestimonialSlim>
    </RestrictedWidth>

    <div class="py-8 flex flex-col gap-y-20">
      <!-- <div class="hidden lg:block mx-5 md:mx-20">
        <ZeroToHeroSection />
      </div> -->

      <RestrictedWidth>
        <div class="flex flex-col gap-y-10">
          <HighLevelScheduleSection />

          <OverviewSection />

          <!-- <TargetAudienceSection /> -->

          <TestimonialSlim image="rashwan-lazkani.jpg" platform="linkedin" url="https://www.linkedin.com/in/rashwan-lazkani" class="my-10">
            <template v-slot:quote>
              <p class="pl-3 pt-2">"This is <span class='underline underline-offset-2 text-red-500'>by far the best course I've ever taken</span>! It covers everything you need to know and more!"</p>
            </template>
            <template v-slot:author>
              <p class="pl-3 flex items-center">- Rashwan Lazkani, Solution Architect at <img src="https://res.cloudinary.com/prsls/image/upload/f_auto,c_scale,h_64/website/logo-wide-polestar.png" class="h-16 inline-block" /></p>
            </template>
          </TestimonialSlim>
        </div>
      </RestrictedWidth>
    </div>

    <!-- only here for the anchor -->
    <div id="testimonials"></div>

    <TrustedBySectionV2 />
    <TestimonialsCloud />

    <!-- only here for the anchor -->
    <div id="pricing" class="mt-20 mb-10">
      <PricingSection ref="pricingSection"/>

      <RestrictedWidth>
        <TestimonialSlim image="marcos-henrique.jpg" platform="linkedin" url="https://www.linkedin.com/in/devopmh/" class="my-10">
          <template v-slot:quote>
            <p class="pl-3 pt-2">"This course gives me deep knowledge in event-driven architectures, now I feel <span class='underline underline-offset-2 text-red-500'>more confident to create serverless solutions</span>."</p>
          </template>
          <template v-slot:author>
            <p class="pl-3 flex items-center">- Marcos Henrique, AWS Community Builder</p>
          </template>
        </TestimonialSlim>
      </RestrictedWidth>
    </div>

    <div class="mb-32 mx-auto md:max-w-6xl">
      <CurriculumSection />
    </div>

    <div id="logos" class="mb-24 hidden sm:flex flex-col gap-y-4">
      <LogosSection />

      <RestrictedWidth>
        <TestimonialSlim image="jordan-crocker.jpg" platform="linkedin" url="https://www.linkedin.com/in/jordan-crocker">
          <template v-slot:quote>
            <p class="pl-3 pt-2">"Yan's workshop allowed our team to get to grips with everything serverless. The exercises around testing were invaluable. <span class='underline underline-offset-2 text-red-500'>This was an excellent investment for our team</span>."</p>
          </template>
          <template v-slot:author>
            <p class="pl-3 flex items-center gap-x-2">- Jordan Crocker, Principal Engineer at <img src="https://res.cloudinary.com/prsls/image/upload/f_auto,c_scale,h_64/website/logo-wide-crowdcube.png" class="h-14 inline-block" /></p>
          </template>
        </TestimonialSlim>
      </RestrictedWidth>
    </div>

    <div id="comparison" class="mb-32">
      <div class="hidden lg:block">
        <ComparedToOthersSectionV3 />
      </div>

      <div class="lg:hidden">
        <ComparedToOthersSectionV1 />
      </div>
    </div>

    <RestrictedWidth>
      <div class="flex flex-col gap-y-20">
        <HappinessGuarantee/>

        <!-- only here for the anchor -->
        <div id="about-me"></div>

        <AboutSection />

        <!-- only here for the anchor -->
        <div id="faq"></div>

        <FaqSection />

        <!-- only here for the anchor -->
        <div id="subscribe"></div>

        <ConvertKitForm ref="convertKitForm"/>
      </div>
    </RestrictedWidth>

    <TestimonialsGrid />

    <FooterSection />

    <CookieOptOutBanner v-bind:page="`Home`" v-bind:pageName="`HomeView`" />

    <!-- <div class="alert-widget-container">
      <AlertWidget
        v-for="(notification, index) of notifications"
        :key="notification.timestamp"
        v-bind:notification="notification"
        class="alert-widget"
        :style="`animation-delay: ${index * 10 + 5}s;`"
        v-on:close-widget="clearNotifications()"
      />
    </div> -->
  </div>
</template>

<script>
import TopBannerV2 from '@/components/TopBannerV2.vue'
import RestrictedWidth from '@/components/home/RestrictedWidth.vue'
import OverviewSection from '@/components/OverviewSection.vue'
// import TargetAudienceSection from '@/components/TargetAudienceSection.vue'
import CurriculumSection from '@/components/CurriculumSection.vue'
import HeroSection from '@/components/HeroSection.vue'
import HighLevelScheduleSection from '@/components/HighLevelScheduleSection.vue'
// import ZeroToHeroSection from '@/components/ZeroToHeroSection.vue'
import ComparedToOthersSectionV1 from '@/components/ComparedToOthersSection.vue'
import ComparedToOthersSectionV3 from '@/components/ComparedToOthersSectionV3.vue'
import PricingSection from '@/components/PricingSection.vue'
import HappinessGuarantee from '@/components/HappinessGuarantee.vue'
import AboutSection from '@/components/AboutSection.vue'
import FaqSection from '@/components/FaqSection.vue'
import FooterSection from '@/components/FooterSection.vue'
import CookieOptOutBanner from '@/components/CookieOptOutBanner.vue'
import TrustedBySectionV2 from '@/components/TrustedBySectionV2.vue'
import TestimonialsCloud from '@/components/TestimonialsCloud.vue'
import TestimonialsGrid from '@/components/TestimonialsGrid.vue'
import TestimonialSlim from '@/components/TestimonialSlim.vue'
import LogosSection from '@/components/LogosSection.vue'
// import AlertWidget from '@/components/notifications/AlertWidget.vue'
import ConvertKitForm from '@/components/home/ConvertKitForm.vue'
import { getCookie, getParameterByName, setCookie } from '../libs/utils'
import { getNotificaitons } from '../libs/notifications'

export default {
  components: {
    TopBannerV2,
    RestrictedWidth,
    HeroSection,
    OverviewSection,
    // TargetAudienceSection,
    CurriculumSection,
    // ZeroToHeroSection,
    HighLevelScheduleSection,
    TrustedBySectionV2,
    PricingSection,
    ComparedToOthersSectionV1,
    ComparedToOthersSectionV3,
    HappinessGuarantee,
    AboutSection,
    FaqSection,
    FooterSection,
    CookieOptOutBanner,
    TestimonialsCloud,
    TestimonialsGrid,
    TestimonialSlim,
    LogosSection,
    ConvertKitForm
    // AlertWidget
  },
  data () {
    return {
      testimonials1: [
        {
          name: 'Craig Balding',
          tagline: 'Cyber Security Consultant',
          quote: 'Yan delivers high-value training that reflects his deep practitioner experience. I gained more in this two day accelerated learning experience than in some 5-day courses.',
          img: 'website/craig-balding.jpg'
        },
        {
          name: 'Murat Ozcan',
          tagline: 'Staff Engineer @ Extend',
          quote: "Yan's content always delivers, being complete not only from a development perspective, but also complete in testing and CI.",
          img: 'website/murat-ozcan.jpg'
        },
        {
          name: 'Andrew Maughan',
          tagline: 'Senior Backend Developer',
          quote: "Yan's Production-Ready Serverless workshop covered a wide range of different AWS technologies in just 2 days. He really opened my eyes to new ways of developing with AWS serverless technology and how I can apply them in my day job.",
          img: 'website/andrew-maughan.jpg'
        },
        {
          name: 'Billy Gibson',
          tagline: 'Cloud Software Engineer',
          quote: 'Yan is incredibly knowledgeable when it comes to AWS and Serverless. His Production-Ready Serverless workshop was of huge benefit to our teams, even those who have already been working with AWS for the past 12 months.',
          img: 'website/billy-gibson.jpg'
        },
        {
          name: 'Quynh Nguyen',
          tagline: 'Engineering Manager @ SimplyBusiness',
          quote: 'We learnt so much in just 2 days and went from zero to having a good understanding of the Serverless landscape.',
          img: 'website/quynh-nguyen.jpg'
        },
        {
          name: 'Ben Quarless',
          tagline: 'Senior Software Engineer',
          quote: 'This course will definitely increase your AWS skill level. This is a informational filled hands-on course on Serverless computing, which delivers a deep dive into AWS services. Complex concepts are explained in a clear and concise manner.',
          img: 'website/ben-quarless.jpg'
        },
        {
          name: 'Manoj Fernando',
          tagline: 'AWS Community Hero',
          quote: 'The workshop was quite informative and hands-on. In fact, the personal experience that Yan had shared during the workshop was extremely valuable to the professionals who run serverless applications in production.',
          img: 'website/manoj-fernando.jpg'
        },
        {
          name: 'Becky Pauley',
          tagline: 'Cloud Engineer',
          quote: 'Learned so much in two days! The way the course and exercises were structured was really helpful, allowing everyone to learn at their own pace with lot of opportunity for hands-on learning. Yan is a great teacher.',
          img: 'website/becky-pauley.jpg'
        }
      ],
      testimonials2: [
        {
          name: 'Arne Haagensen',
          tagline: 'Head of Public Cloud',
          quote: 'Yan took us through a proper deep dive into the typical AWS services leveraged in serverless solutions built on AWS. Exceptional pairing when questions arrived, and took time to discuss our needs/issues.',
          img: 'website/arne-haagensen.jpg'
        },
        {
          name: 'Martijn van der Kamp',
          tagline: 'DevOps Engineer',
          quote: 'I found the workshop very informative and practical. In other AWS courses, you only learn about the products. But in this workshop, you learn how to use them in the real-world. Yan gave me the building blocks that I can use in my own projects!',
          img: 'website/martin-van-der-kamp.jpg'
        },
        {
          name: 'Kaarel Sööt',
          tagline: 'CTO @ Project Nebula',
          quote: "The workshop was very informative, both in terms of validating some approaches I've taken, and also showing how to improve in other areas. Yan shared his personal experiences with serverless and offered advice on specific challenges.",
          img: 'website/logo-kaarel-soot.png'
        },
        {
          name: 'Farzana Rahman',
          tagline: 'AWS Community Builder',
          quote: 'Best part of this course is you can discuss with Yan about your own production related problems. Thank you so much Yan for your effort.',
          img: 'website/logo-farzana-rahman.png'
        },
        {
          name: 'Mosleh Alnakib',
          tagline: 'Software Engineer',
          quote: 'It has been a truly invaluable learning experience for me. it really is an amazing course and covered very important parts. I would recommend this course to anyone who wants to better understand serverless and AWS.',
          img: 'website/mosleh-alnakib.jpg'
        },
        {
          name: 'Jasper Gabriel',
          tagline: 'Lead Software Engineer',
          quote: "As someone who has learned serverless on his own, going through Yan's workshop gave me a lot of practical lessons and best practices that I can't wait to implement on my own projects. Yan has also been super helpful, and he answers all questions thrown at him.",
          img: 'website/jasper-gabriel.jpg'
        },
        {
          name: 'Subhamay Bhattacharyya',
          tagline: 'AWS Data Engineer',
          quote: 'This is a unique experience packed with lots of advanced Serverless topics. The exercises covers lots of good information on managing AWS costs, improving performance and many more. I recommend this course to anyone who is looking to take their serverless skills to the next level.',
          img: 'website/subhamay-bhattacharyya.jpg'
        },
        {
          name: 'Bruno Ripa',
          tagline: 'Independent Consultant',
          quote: 'I took this workshop to take advantage of the massive experience Yan has in serverless; using such a technology can present challenges that require real world experiences, and Yan is always able to provide the best insights anytime I have any doubt.',
          img: 'website/bruno-ripa.jpg'
        }
      ],
      notifications: []
    }
  },
  async mounted () {
    let affiliateId = getCookie('affiliateId')
    if (!affiliateId) {
      affiliateId = getParameterByName('affiliateid')
      if (affiliateId) {
        // Set affiliateId in cookie for 30 days if it's found in the query string parameter
        setCookie('affiliateId', affiliateId, 30)
      }
    }

    const notifications = await getNotificaitons()
    notifications.forEach(x => this.notifications.push(x))
  },
  methods: {
    clearNotifications () {
      this.notifications = []
    },

    onPppAvailable (amount) {
      this.$refs.pricingSection.handlePppDiscount(amount)
      this.$refs.convertKitForm.handlePppDiscount(amount)
    },

    onDiscountAvailable (holiday, amount, couponCode) {
      this.$refs.pricingSection.handleHolidayDiscount(holiday, amount, couponCode)
      this.$refs.convertKitForm.handleHolidayDiscount(amount)
    }
  }
}
</script>

<style scoped>
  .alert-widget-container {
    position: fixed;
    bottom: -100px;
    left: 15px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .alert-widget {
    animation: slideIn 8s ease-in-out forwards
  }

  @keyframes slideIn {
    0% {
      transform: translateY(0%);
    }
    10% {
      transform: translateY(-250%);
    }
    90% {
      transform: translateY(-250%);
    }
    100% {
      transform: translateY(0%);
    }
  }
</style>
